import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AdminApiService } from '../../services/admin-api.service';
import { InviteUserModalComponent } from '../invite-user-modal/invite-user-modal.component';
import { AssessmentSession } from '../user-score-drawer/user-score-drawer.component';
import { forkJoin } from 'rxjs';
import { RecruitmentApiService } from '../../services/recruitment-api.service';
import { RecruitmentOrganization } from '../../recruitment/models/recruitment.models';

export interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  createdAt: string;
  assessmentAttemptBlocked?: boolean;
}

@Component({
  selector: 'app-list-users',
  templateUrl: './list-users.component.html',
  styleUrls: ['./list-users.component.css'],
  standalone: false
})
export class ListUsersComponent implements OnInit {
  displayedColumns: string[] = ['userId', 'name', 'email', 'role', 'status', 'scores', 'delete'];
  dataSource: User[] = [];
  loadingUsers = false;
  readonly userPageSize = 25;
  userPageNumber = 1;
  private currentUserCursor: string | null = null;
  private previousUserCursors: Array<string | null> = [];
  nextUserCursor: string | null = null;
  loadingScores: { [userId: string]: boolean } = {};
  scoreDrawerOpen = false;
  selectedUser: User | null = null;
  selectedUserSessions: AssessmentSession[] = [];
  selectedUserScoreError = '';
  savingStateSessionId: string | null = null;
  savingAssessmentSessionId: string | null = null;
  savingAttemptAccess = false;
  selectedOrganization: RecruitmentOrganization | null = null;
  organizationError = '';
  loadingOrganization = false;

  constructor(
    private adminApi: AdminApiService,
    private recruitmentApi: RecruitmentApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.loadingUsers = true;
    this.adminApi.getUsers(this.userPageSize, this.currentUserCursor).subscribe({
      next: (res) => {
        this.dataSource = res.users || [];
        this.nextUserCursor = res.nextCursor || null;
        this.loadingUsers = false;
      },
      error: (err) => {
        console.error('Failed to load users', err);
        this.snackBar.open('Failed to load users', 'Dismiss', { duration: 3000 });
        this.loadingUsers = false;
      }
    });
  }

  nextUserPage(): void {
    if (!this.nextUserCursor || this.loadingUsers) return;
    this.previousUserCursors.push(this.currentUserCursor);
    this.currentUserCursor = this.nextUserCursor;
    this.userPageNumber += 1;
    this.loadUsers();
  }

  previousUserPage(): void {
    if (this.userPageNumber <= 1 || this.loadingUsers) return;
    this.currentUserCursor = this.previousUserCursors.pop() ?? null;
    this.userPageNumber -= 1;
    this.loadUsers();
  }

  openInviteModal(): void {
    const dialogRef = this.dialog.open(InviteUserModalComponent, {
      width: '460px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.snackBar.open('User invited successfully!', 'Close', { duration: 3000 });
        this.loadUsers(); // refresh table
      }
    });
  }

  getScores(user: User): void {
    this.selectedUser = user;
    this.selectedUserSessions = [];
    this.selectedUserScoreError = '';
    this.scoreDrawerOpen = true;
    this.loadingScores[user.userId] = true;

    forkJoin({
      scores: this.adminApi.getUserScores(user.userId),
      user: this.adminApi.getUserById(user.userId)
    }).subscribe({
      next: ({ scores, user: userResponse }) => {
        this.loadingScores[user.userId] = false;
        this.selectedUserSessions = scores.allUserScores || [];
        const refreshedUser: User = userResponse.user || user;
        this.selectedUser = refreshedUser;
        this.dataSource = this.dataSource.map((item) =>
          item.userId === user.userId ? { ...item, ...refreshedUser } : item
        );
      },
      error: (err) => {
        console.error('Failed to get scores', err);
        this.loadingScores[user.userId] = false;
        this.selectedUserScoreError = 'Failed to load score sessions.';
        this.snackBar.open('Failed to load scores', 'Dismiss', { duration: 3000 });
      }
    });
  }

  viewOrganization(user: User): void {
    this.selectedUser = user;
    this.selectedOrganization = null;
    this.organizationError = '';
    this.loadingOrganization = true;
    this.scoreDrawerOpen = true;

    this.recruitmentApi.getRecruiterOrganization(user.userId).subscribe({
      next: (organization) => {
        this.selectedOrganization = organization;
        this.loadingOrganization = false;
      },
      error: (err) => {
        console.error('Failed to load recruiter organization', err);
        this.organizationError = err?.error?.error?.message || 'Failed to load organization.';
        this.loadingOrganization = false;
      }
    });
  }

  openRecruitmentWorkspace(path: 'dashboard' | 'benchmarks'): void {
    if (!this.selectedOrganization) {
      return;
    }
    const commands = path === 'benchmarks' ? ['/recruitment/benchmarks'] : ['/recruitment'];
    this.router.navigate(commands, {
      queryParams: { orgId: this.selectedOrganization.orgId }
    });
  }

  saveAssessmentAttemptAccess(blocked: boolean): void {
    if (!this.selectedUser) {
      return;
    }

    const userId = this.selectedUser.userId;
    this.savingAttemptAccess = true;
    this.adminApi.setAssessmentAttemptBlocked(userId, blocked).subscribe({
      next: (res) => {
        const updatedUser = res.user || { ...this.selectedUser, assessmentAttemptBlocked: blocked };
        this.selectedUser = updatedUser;
        this.dataSource = this.dataSource.map((user) =>
          user.userId === userId ? { ...user, ...updatedUser } : user
        );
        this.savingAttemptAccess = false;
        this.snackBar.open(blocked ? 'Assessment attempts blocked' : 'Assessment attempt allowed', 'Close', {
          duration: 2500
        });
      },
      error: (err) => {
        console.error('Failed to update assessment attempt access', err);
        this.savingAttemptAccess = false;
        this.snackBar.open('Failed to update assessment attempt access', 'Dismiss', { duration: 3000 });
      }
    });
  }

  closeScoreDrawer(): void {
    this.scoreDrawerOpen = false;
    this.selectedOrganization = null;
    this.organizationError = '';
  }

  openDashboard(session: AssessmentSession): void {
    const userId = session.userId || this.selectedUser?.userId;
    if (!userId || !session.sessionId) {
      this.snackBar.open('Missing user or session id for dashboard', 'Dismiss', { duration: 3000 });
      return;
    }

    this.router.navigate(['/user-dashboard'], {
      queryParams: {
        userId,
        sessionId: session.sessionId
      }
    });
  }

  saveAssessmentState(event: { sessionId: string; state: string }): void {
    if (!this.selectedUser) {
      return;
    }

    this.savingStateSessionId = event.sessionId;
    this.adminApi.setAssessmentState(this.selectedUser.userId, event.sessionId, event.state).subscribe({
      next: () => {
        this.selectedUserSessions = this.selectedUserSessions.map((session) =>
          session.sessionId === event.sessionId
            ? { ...session, assessmentState: event.state }
            : session
        );
        this.savingStateSessionId = null;
        this.snackBar.open('Assessment state updated', 'Close', { duration: 2500 });
      },
      error: (err) => {
        console.error('Failed to save assessment state', err);
        this.savingStateSessionId = null;
        this.snackBar.open('Failed to save assessment state', 'Dismiss', { duration: 3000 });
      }
    });
  }

  saveAssessmentObject(event: { session: AssessmentSession; flag: 'CREATE-NEW' | 'UPDATE-OLD' }): void {
    const userId = event.session.userId || this.selectedUser?.userId;
    if (!userId) {
      return;
    }

    this.savingAssessmentSessionId = event.session.sessionId;
    this.adminApi.setAssessmentObject(userId, event.session.sessionId, event.session, event.flag).subscribe({
      next: () => {
        if (event.flag === 'UPDATE-OLD') {
          this.selectedUserSessions = this.selectedUserSessions.map((session) =>
            session.sessionId === event.session.sessionId ? { ...event.session } : session
          );
        } else {
          this.selectedUserSessions = [event.session, ...this.selectedUserSessions];
        }

        this.savingAssessmentSessionId = null;
        this.snackBar.open(`Assessment ${event.flag === 'CREATE-NEW' ? 'created' : 'updated'}`, 'Close', {
          duration: 2500
        });
      },
      error: (err) => {
        console.error('Failed to save assessment object', err);
        this.savingAssessmentSessionId = null;
        this.snackBar.open('Failed to save assessment object', 'Dismiss', { duration: 3000 });
      }
    });
  }

  deleteUser(user: User): void {
    if (!confirm(`Delete ${user.firstName} ${user.lastName}? This cannot be undone.`)) return;

    this.adminApi.deleteUser(user.userId).subscribe({
      next: (res) => {
        if (res.success) {
          this.dataSource = this.dataSource.filter(u => u.userId !== user.userId);
          this.snackBar.open('User deleted', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'Failed to delete user', 'Dismiss', { duration: 3000 });
        }
      },
      error: (err) => {
        console.error('Failed to delete user', err);
        this.snackBar.open('Failed to delete user', 'Dismiss', { duration: 3000 });
      }
    });
  }
}
